import { CategorySections, Category, Zone } from "@apis/index";
import AccessDenied from "@components/access-denied";
import { deletesvg, edit, switchOff, switchOn } from "@assets/icons";
import Column from "@components/column";
import DeleteModal from "@components/delete-modal";
import Svg from "@components/svg";
import Table from "@components/table";
import { useModal } from "@context/ModalContext";
import { RootState } from "@store/index";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueryState } from "@hooks/useQueryState";

const SectionTitleTable = ({ moduleId }) => {
  const {
    items: categorySections = [],
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.categorySections);

  const { data: zones = [] } = useSelector((state: RootState) => state.zones);
  const { data: categories = [] } = useSelector(
    (state: RootState) => state.categories,
  );

  const { showModal } = useModal();
  const [page, setPage] = useQueryState<number>("page", 1);
  const [search, setSearch] = useState("");


  const [error, setError] = useState<string | null>(null);

  const fetchSectionTitles = useCallback(async () => {
    setError(null);
    try {
      await CategorySections.list({
        state: "categorySections",
        params: { page, moduleId, limit: 30, search },
      });
    } catch (err: any) {
      console.error("Failed to fetch section titles:", err);
      setError(err.message || "Failed to load section titles");
    }
  }, [moduleId, page, search]);

  useEffect(() => {
    fetchSectionTitles();
  }, [fetchSectionTitles]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const columns = [
    {
      title: "ID",
      key: "id",
      render: (item: any) => item.id,
    },
    {
      title: "Section Title",
      key: "title",
      render: (item: any) => item.title,
    },
    {
      title: "Store Type",
      key: "type",
      render: (item: any) => item?.type?.name || "N/A",
    },
    {
      title: "Zones",
      key: "zones",
      render: (item: any) => {
        return item.zones?.map((z: any) => z.name).join(", ") || "N/A";
      },
    },
    {
      title: "Categories",
      key: "categories",
      render: (item: any) => {
        return item.categories?.map((c: any) => c.name).join(", ") || "N/A";
      },
    },
    {
      title: "Priority",
      key: "priority",
      render: (item: any) => item.priority ?? "N/A",
    },
    {
      title: "Active",
      key: "isActive",
      render: (item: any) => (
        <Svg
          src={item.isActive ? switchOn : switchOff}
          onClick={async () => {
            await CategorySections.update({
              state: "categorySections",
              id: item.id,
              data: { isActive: !item.isActive },
            })
              .then(() => fetchSectionTitles())
              .catch((err) => toast.error(err.message));
          }}
        />
      ),
    },
  ];

  const navigate = useNavigate();

  if (error) {
    return (
      <AccessDenied
        message={error}
        onRetry={fetchSectionTitles}
      />
    );
  }

  return (
    <Table
      data={categorySections}
      columns={columns}
      totalItems={totalItems}
      totalPages={totalPages}
      pageControlled={page}
      onPageChange={setPage}
      itemsPerPage={30}
      hasSearchbar={true}
      onSearchChange={setSearch}
      actions={[
        {
          key: "edit",
          icon: edit,
          onClick: (item) => {
            navigate(
              `/admin/product-management/categories?module=${item.moduleId}`,
              {
                state: { ...item, isSectionTitleEdit: true },
              },
            );
            window.scrollTo(0, 0);
          },
        },
        {
          key: "remove",
          icon: deletesvg,
          onClick: async (item) => {
            showModal(
              <DeleteModal
                state={"categorySections"}
                api={CategorySections}
                id={item.id}
                onSuccess={() => fetchSectionTitles()}
              />,
            );
          },
        },
      ]}
    />
  );
};

export default SectionTitleTable;
